"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import "./AdminDashboard.css"

function getYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

const Testimonies = () => {
  const [testimonies, setTestimonies] = useState([])
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterType, setFilterType] = useState("all")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadTestimonies()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadTestimonies = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getTestimonies()
      // Sort testimonies by creation date in descending order (newest first)
      const sortedTestimonies = response.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
      setTestimonies(sortedTestimonies)
    } catch (error) {
      console.error('Error loading testimonies:', error)
      setError(error.message || 'Failed to load testimonies')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value)
  }

  const handleTypeFilterChange = (e) => {
    setFilterType(e.target.value)
  }

  const deleteTestimony = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimony?")) {
      try {
        setIsLoading(true)
        setError("")
        await apiService.deleteTestimony(id)
        setSuccessMessage("Testimony deleted successfully!")
        loadTestimonies()
      } catch (error) {
        console.error('Error deleting testimony:', error)
        setError(error.message || 'Failed to delete testimony')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const filteredTestimonies = testimonies.filter((testimony) => {
    const matchesStatus = filterStatus === "all" || testimony.status === filterStatus
    const matchesType = filterType === "all" || (testimony.video ? "video" : "text") === filterType
    return matchesStatus && matchesType
  })

  return (
    <div className="testimonies-page">
      {error && <div className="error-message">{error}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="filters-container">
        <div className="filter-box">
          <select
            value={filterStatus}
            onChange={handleStatusFilterChange}
            className="form-control"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="filter-box">
          <select
            value={filterType}
            onChange={handleTypeFilterChange}
            className="form-control"
          >
            <option value="all">All Types</option>
            <option value="text">Text</option>
            <option value="video">Video</option>
          </select>
        </div>
      </div>

      <h2 className="section-title">Testimonies</h2>

      {isLoading ? (
        <div className="loading">Loading testimonies...</div>
      ) : filteredTestimonies.length > 0 ? (
        filteredTestimonies.map((testimony) => (
          <div className="testimony-card card" key={testimony.id}>
            <div className="testimony-header">
              <h3 className="testimony-title">{testimony.title}</h3>
              <span className={`status-badge status-${testimony.status}`}>
                {testimony.status.charAt(0).toUpperCase() +
                  testimony.status.slice(1)}
              </span>
            </div>

            <div className="testimony-content">
              <div className="testimony-meta">
                <p className="testimony-author">By: {testimony.name}</p>
                <p className="testimony-date">
                  Date: {new Date(testimony.created_at).toLocaleDateString()}
                </p>
              </div>

              {testimony.video ? (
                <div className="testimony-video" style={{ marginBottom: 16 }}>
                  {testimony.video.match(/(youtube\.com|youtu\.be)/) ? (
                    <iframe
                      width="100%"
                      height="250"
                      src={`https://www.youtube.com/embed/${getYouTubeId(
                        testimony.video
                      )}`}
                      title="Testimony Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        borderRadius: 8,
                        width: "100%",
                        maxWidth: 400,
                        background: "#000",
                      }}
                    />
                  ) : (
                    <video
                      width="100%"
                      height="250"
                      controls
                      style={{
                        borderRadius: 8,
                        width: "100%",
                        maxWidth: 400,
                        background: "#000",
                      }}
                    >
                      <source src={testimony?.video} type="video/mp4" />
                      <source src={testimony?.video} type="video/webm" />
                      <source
                        src={testimony?.video}
                        type="video/ogg"
                      />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              ) : (
                <p className="testimony-text">{testimony.description}</p>
              )}
            </div>

            <div className="testimony-actions">
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => deleteTestimony(testimony.id)}
              >
                <i className="material-icons">delete</i> Delete
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="no-results">
          <p>No testimonies found matching your filter criteria.</p>
        </div>
      )}
    </div>
  );
}

export default Testimonies
